import { useCallback, useEffect, useRef, useState } from "react";
import axios from "../utils/axios";

// Server-side infinite-scroll data source for the inventory reports (Stock Transfer,
// Requisition). Renders PAGE_SIZE (50) records by default and appends the next page
// as the user scrolls. All filters are pushed to the server so pagination stays
// consistent. `collectAll` pages through the full result set for export.

// Any set of server-side filter params (key → value); empty values are dropped.
export type ReportFilters = Record<string, string | undefined>;

const PAGE_SIZE = 50;
const EXPORT_LIMIT = 200; // backend hard cap per page

const buildQuery = (f: ReportFilters, page: number, limit: number) => {
  const p: string[] = [];
  Object.entries(f).forEach(([k, v]) => { if (v) p.push(`${k}=${encodeURIComponent(v)}`); });
  p.push(`page=${page}`);
  p.push(`limit=${limit}`);
  return p.join("&");
};

export function useInfiniteReport(baseUrl: string, filters: ReportFilters) {
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);      // first page (filters changed)
  const [loadingMore, setLoadingMore] = useState(false); // subsequent pages
  const key = JSON.stringify(filters);
  const busy = useRef(false);

  // Reset and load the first page whenever the filters change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setRows([]);
    setPage(0);
    axios.get(`${baseUrl}?${buildQuery(filters, 0, PAGE_SIZE)}`)
      .then((res) => {
        if (cancelled) return;
        setRows(res.data?.data || []);
        setTotal(res.data?.total || 0);
      })
      .catch(() => { if (!cancelled) { setRows([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [key, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMore = rows.length < total;

  const loadMore = useCallback(async () => {
    if (busy.current || loading || rows.length >= total) return;
    busy.current = true;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const res = await axios.get(`${baseUrl}?${buildQuery(filters, next, PAGE_SIZE)}`);
      setRows((prev) => [...prev, ...(res.data?.data || [])]);
      setTotal(res.data?.total || 0);
      setPage(next);
    } catch { /* ignore — keep what we have */ } finally {
      setLoadingMore(false);
      busy.current = false;
    }
  }, [baseUrl, key, page, loading, rows.length, total]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page through the entire filtered result set (for export). Does not touch state.
  const collectAll = useCallback(async (): Promise<any[]> => {
    const all: any[] = [];
    let pg = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const res = await axios.get(`${baseUrl}?${buildQuery(filters, pg, EXPORT_LIMIT)}`);
      const data = res.data?.data || [];
      const tot = res.data?.total || 0;
      all.push(...data);
      if (data.length === 0 || all.length >= tot) break;
      pg += 1;
    }
    return all;
  }, [baseUrl, key]); // eslint-disable-line react-hooks/exhaustive-deps

  return { rows, total, loading, loadingMore, hasMore, loadMore, collectAll };
}
